import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserService } from '@services/user.service';
import { isHttpUrl } from '@shared/utils/http-url.utils';
import { Observable, take } from 'rxjs';
import { SessionStorageService } from './storage.service';

@Injectable({
	providedIn: 'root',
})
export class SymbolService {
	constructor(
		private httpClient: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {}

	private headers() {
		const accountId: string = this.userService.account.id;
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
			'x-account-id': accountId,
		});
	}

	getAll(includeAllLanguages: boolean = true): Observable<{ result: ISymbolGetResponse[] }> {
		const apiUrl = `${environment.awsEnvUri}/drawing/engines/${this.userService.account.defaultEngine}/symbols?allLanguages=${includeAllLanguages}`;
		return this.httpClient.get<{ result: ISymbolGetResponse[] }>(apiUrl, {
			headers: this.headers(),
		});
	}

	get(name: string, language?: string): Observable<{ result: ISymbolGetResponse[] }> {
		const apiUrl = `${environment.awsEnvUri}/drawing/engines/${this.userService.account.defaultEngine}/symbols/${name}${language ? `?language=${language}` : ''}`;
		return this.httpClient.get<{ result: ISymbolGetResponse[] }>(apiUrl, {
			headers: this.headers(),
		});
	}

	getSignedUrls(ids: string[]): Observable<{ result: ISymbolSignedUrlResponse[] }> {
		const apiUrl = `${environment.awsEnvUri}/drawing/engines/${this.userService.account.defaultEngine}/symbols/signed-urls`;
		return this.httpClient.post<{ result: ISymbolSignedUrlResponse[] }>(apiUrl, { ids }, { headers: this.headers() });
	}

	createAndGetUploadUrl(symbolData: ISymbolPostRequest): Observable<IResult<ISymbolPostResponse>> {
		const apiUrl = `${environment.awsEnvUri}/drawing/engines/${this.userService.account.defaultEngine}/symbols`;
		return this.httpClient.post<IResult<ISymbolPostResponse>>(apiUrl, symbolData, {
			headers: this.headers(),
		});
	}

	createAndCopy(symbolData: ISymbolCopyPostRequest): Observable<IResult<ISymbolPostResponse>> {
		const apiUrl = `${environment.awsEnvUri}/drawing/engines/${this.userService.account.defaultEngine}/symbols`;
		// The backend's XSS filter rejects the raw & of a presigned url, so a url is sent percent-encoded (and decoded there); a key is sent as is.
		const assetKey = isHttpUrl(symbolData.assetKey) ? encodeURIComponent(symbolData.assetKey) : symbolData.assetKey;
		return this.httpClient.post<IResult<ISymbolPostResponse>>(
			apiUrl,
			{ ...symbolData, assetKey },
			{
				headers: this.headers(),
			},
		);
	}

	updateAndGetUploadUrl(symbolData: ISymbolPutRequest, id: string): Observable<IResult<ISymbolPutResponse>> {
		const apiUrl = `${environment.awsEnvUri}/drawing/engines/${this.userService.account.defaultEngine}/symbols/${id}`;
		return this.httpClient.put<IResult<ISymbolPutResponse>>(apiUrl, symbolData, {
			headers: this.headers(),
		});
	}

	uploadFile(uploadUrl: string, fileData: any): Observable<any> {
		return this.httpClient.put(uploadUrl, fileData).pipe(take(1));
	}

	delete(id: string, language?: string): Observable<any> {
		const apiUrl = `${environment.awsEnvUri}/drawing/engines/${this.userService.account.defaultEngine}/symbols/${id}${language ? `?language=${language}` : ''}`;
		return this.httpClient.delete(apiUrl, { headers: this.headers() });
	}
}

export interface IResult<T> {
	result: T;
}

export interface ISymbolGetResponse {
	id: string;
	name: string;
	description: string;
	height: number;
	width: number;
	basePointX: number;
	basePointY: number;
	drawingOrder: number;
	downloadUrl: string;
	language?: string;
	isDefault?: boolean;
}

export interface ISymbolPostRequest {
	name: string;
	description: string;
	basePointX: number;
	basePointY: number;
	height: number;
	width: number;
	fileName: string;
	drawingOrder?: number;
	language?: string;
	isDefault?: boolean;
}

export interface ISymbolCopyPostRequest {
	name: string;
	description: string;
	basePointX: number;
	basePointY: number;
	height: number;
	width: number;
	drawingOrder: number;
	assetKey: string;
	language?: string;
	isDefault?: boolean;
}

export interface ISymbolPostResponse {
	id: string;
	name: string;
	description: string;
	height: number;
	width: number;
	basePointX: number;
	basePointY: number;
	drawingOrder: number;
	uploadUrl: string;
	language?: string;
	isDefault?: boolean;
}

export interface ISymbolPutRequest {
	id: string;
	name: string;
	description: string;
	basePointX: number;
	basePointY: number;
	height: number;
	width: number;
	fileName: string;
	language?: string;
	oldLanguage?: string;
	isDefault?: boolean;
}

export interface ISymbolPutResponse {
	id: string;
	name: string;
	description: string;
	height: number;
	width: number;
	basePointX: number;
	basePointY: number;
	uploadUrl: string;
	language?: string;
	isDefault?: boolean;
}

export interface ISymbolSignedUrlResponse {
	id: string;
	url: string;
}

export interface ISymbolExport {
	name: string;
	description: string;
	width: number;
	height: number;
	basePointX: number;
	basePointY: number;
	drawingOrder: number;
	assetKey: string;
	language?: string;
	isDefault?: boolean;
}
